import { createCommand, type EditorState, type LexicalEditor } from 'lexical';

import { TEXT_BOX_SELECTOR, type NoteTextOwner } from '../../config';
import { readNoteHistoryKey } from '../../lib';

// Commits the text typed into the document that waits for a pause, as
// before an undo; the texts given are dropped instead, as a deletion that
// sends their whole text drops them.
export const FLUSH_NOTE_TEXT_COMMAND = createCommand<NoteTextOwner[] | void>(
  'FLUSH_NOTE_TEXT_COMMAND',
);

// The next note the document gets also changes the line under the caret, as
// the note an undo or a redo brings back.
export const RELEASE_NOTE_CARET_COMMAND = createCommand<void>(
  'RELEASE_NOTE_CARET_COMMAND',
);

// The states an edit that changed the note's structure left, which the
// document reverts: nothing reads them as typed text.
const rejectedStates = new WeakSet<EditorState>();

export const rejectNoteEdit = (state: EditorState) => rejectedStates.add(state);

export const isNoteEditRejected = (state: EditorState) =>
  rejectedStates.has(state);

// A hosted widget, a line's lead or tail and a section's head are the parts
// of the document that are not its text.
const WIDGET_PART = '[data-lexical-decorator], [contenteditable="false"]';

export const isNoteWidgetEvent = (
  editor: LexicalEditor,
  event: Event | null,
) => {
  const target = event?.target;
  const part = target instanceof Element && target.closest(WIDGET_PART);
  return !!part && part !== editor.getRootElement();
};

// The note's undo and redo keys on a widget's button, checkbox or radio
// still act on the note; a text box in the widget keeps them for its own
// text. The editable document around the widget is not that text box.
export const isHistoryKeyOutsideTextBox = (event: Event | null) => {
  if (!(event instanceof KeyboardEvent) || !readNoteHistoryKey(event)) {
    return false;
  }
  const target = event.target instanceof Element ? event.target : null;
  const box = target?.closest(TEXT_BOX_SELECTOR);
  const part = target?.closest(WIDGET_PART);
  return !box || !part?.contains(box);
};
